import React, { useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Text,
  useTheme,
  Box,
  Tooltip,
  Flex,
} from "@chakra-ui/react";
import { TbEdit } from "react-icons/tb";
import { MdOutlineDeleteOutline, MdOutlineAssignment } from "react-icons/md";
import DeletePopup from "../../popups/DeletePopup";
import ChangeStatusPopup from "../../popups/ChangeStatusPopup";
import { deleteTimetable } from "../../../store/slice/timetableSlice";
import { deleteAssignment } from "../../../store/slice/assignmentSlice";
import { useDispatch } from "react-redux";
import OpenFileModal from "../../modals/OpenFileModal/OpenFileModal";
import TimeTableFileModal from "../../modals/OpenFileModal/TimeTableFileModal";
import { useToastContext } from "../../toastNotificationProvider/ToastNotificationProvider";

const AssignmentTable = ({
  data,
  extraColumns = [],
  columnOrder = [],
  onEdit,
  entityType,
  limit,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { addNotification } = useToastContext();

  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFileModalOpen, setFileModalOpen] = useState(false);
  const [isTimeFileModalOpen, setTimeFileModalOpen] = useState(false);
  const [selectedOriFiles, setSelectedOriFiles] = useState([]);
  const [selectedTempFiles, setSelectedTempFiles] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = limit;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const {
    editButtonColor,
    deleteBtnColor,
    primaryBlueLight,
    activeButtonTextColor,
    activeStatusColor,
  } = theme.colors.pallete;

  const fixedColumns = [
    { label: "S.No", key: "sno" },
    {
      label: "Subject",
      key: "subject",
      render: (row) => row.subjectName || "—",
    },
    { label: "Topic", key: "topic", render: (row) => row.topicName || "—" },
    { label: "Description", key: "description" },
    {
      label: "Uploaded Files",
      key: "files",
      render: (row) => {
        let fileCount = 0;
        if (entityType === "assignment") {
          fileCount = Array.isArray(row?.assignmentFiles)
            ? row?.assignmentFiles.length
            : 0;
        } else {
          fileCount = Array.isArray(row.files) ? row.files.length : 0;
        }

        return (
          <HStack
            cursor="pointer"
            color={primaryBlueLight}
            onClick={() => {
              if (entityType === "assignment") {
                setSelectedOriFiles(row?.assignmentFiles || []);
                setSelectedTempFiles(row.files || []);
                setFileModalOpen(true);
              } else {
                setSelectedTempFiles(row.files || []);
                setTimeFileModalOpen(true);
              }
            }}
          >
            <MdOutlineAssignment size={20} />
            <Text fontSize="12px" fontWeight="500">
              Open File {fileCount > 1 ? `+${fileCount - 1}` : ""}
            </Text>
          </HStack>
        );
      },
    },
    {
      label: "Status",
      key: "status",
      render: () => (
        <Button
          w={"100px"}
          h={"30px"}
          fontSize={"14px"}
          fontWeight={500}
          bg={activeStatusColor}
          color={activeButtonTextColor}
          _hover={{ opacity: 0.8 }}
        >
          Uploaded
        </Button>
      ),
    },
  ];

  let allColumns = [...fixedColumns, ...extraColumns];

  if (columnOrder.length > 0) {
    allColumns = columnOrder
      .map((key) => allColumns.find((col) => col.key === key))
      .filter(Boolean);
  }

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeletePopupOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow) {
      try {
        if (entityType === "timetable") {
          await dispatch(deleteTimetable(selectedRow.id));
        } else if (entityType === "assignment") {
          await dispatch(deleteAssignment(selectedRow.id));
        }
        setDeletePopupOpen(false);
        addNotification(
          `${
            entityType.charAt(0).toUpperCase() + entityType.slice(1)
          } deleted successfully`,
          "success",
          3000
        );
      } catch (error) {
        console.error("Error deleting:", error);
      }
    }
  };

  return (
    <Box overflowX="auto" mt={6}>
      <Table variant="unstyled">
        <Thead>
          <Tr>
            {allColumns.map((col, index) => (
              <Th whiteSpace="nowrap" key={index}>
                {col.label}
              </Th>
            ))}
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {paginatedData.map((row, rowIndex) => (
            <Tr key={rowIndex}>
              {allColumns.map((col, colIndex) => (
                <Td key={colIndex}>
                  {col.render ? col.render(row) : row[col.key] || "—"}
                </Td>
              ))}
              <Td>
                <HStack>
                  {entityType === "timetable" ? (
                    <Tooltip label="Edit not available for timetable" hasArrow>
                      <span>
                        <Button
                          p={0}
                          variant="ghost"
                          color={editButtonColor}
                          isDisabled
                        >
                          <TbEdit size={"20px"} />
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      p={0}
                      onClick={() => onEdit(row.id)}
                      variant="ghost"
                      color={editButtonColor}
                    >
                      <TbEdit size={"20px"} />
                    </Button>
                  )}
                  <Button
                    p={0}
                    color={deleteBtnColor}
                    onClick={() => handleDeleteClick(row)}
                    variant="ghost"
                  >
                    <MdOutlineDeleteOutline size={"20px"} />
                  </Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* Pagination Controls */}
      <Flex justify="center" align="center" m={10} gap={4}>
        <Button
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>

        <Text fontSize="sm" fontWeight="medium">
          {currentPage} / {totalPages}
        </Text>

        <Button
          size="sm"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      {/* Modals and Popups */}
      <DeletePopup
        isOpen={isDeletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onDelete={handleConfirmDelete}
        text={`the ${entityType}`}
      />

      <ChangeStatusPopup
        isOpen={isStatusPopupOpen}
        onClose={() => setStatusPopupOpen(false)}
        onClick={handleConfirmDelete}
        text={` ${entityType}`}
      />

      <OpenFileModal
        isOpen={isFileModalOpen}
        onClose={() => setFileModalOpen(false)}
        tempfiles={selectedTempFiles}
        orifiles={selectedOriFiles}
        entity={entityType}
        docType={entityType}
      />

      <TimeTableFileModal
        isOpen={isTimeFileModalOpen}
        onClose={() => setTimeFileModalOpen(false)}
        files={selectedTempFiles}
      />
    </Box>
  );
};

export default AssignmentTable;
